import environments from './config';

class Ramp {
  constructor() {
    this.modal = null;
    this.config = {};

    // 1. FIX: Store bound handlers for correct event listener removal
    this.boundHandleReadyMessage = this.handleReadyMessage.bind(this);
    this.boundHandleSuccess = this.handleSuccess.bind(this);
    this.boundHandleWalletDetails = this.handleWalletDetails.bind(this);
    this.boundHandleKeydown = this.handleKeydown.bind(this); // For Escape key
    this.boundHandleOverlayClick = this.handleOverlayClick.bind(this); // For modal dismissal
    this.boundHandleCloseMessage = this.handleCloseMessage.bind(this); // For iframe initiated close
  }

  // Helper function for message validation (Security Fix)
  isValidMessage(event, expectedType) {
    // FIX 2: Strict security check for origin
    return (
      event.origin === environments.CHECKOUT_URL &&
      event.data &&
      event.data.type === expectedType
    );
  }

  initialize(config) {
    // 3. FIX: Ensure any existing instance is cleaned up before initialization
    this.destroy();

    // Store configuration
    this.config = config;

    // Create the iframe modal
    this.createIframe();

    // Add event listeners (now using centralized, security-focused listeners)
    this.setupListeners();
  }

  setupListeners() {
    // Set up listeners for messages from the iframe (using bound handlers)
    window.addEventListener('message', this.boundHandleReadyMessage, false);
    window.addEventListener('message', this.boundHandleSuccess);
    window.addEventListener('message', this.boundHandleWalletDetails);
    window.addEventListener('message', this.boundHandleCloseMessage);

    // Set up listeners for global UI dismissal (using bound handlers)
    document.addEventListener('keydown', this.boundHandleKeydown);

    // The overlay click listener is set up in createIframe and uses a bound handler
    // The internal iframe resize listener remains in createIframe but should also be cleaned up.
  }

  getSerializableConfig() {
    const serializableConfig = {
      ...this.config,
      reference: this.config.reference,
    };

    const functionProps = [
      'onClose',
      'onReceiveWalletDetails',
      'onSuccess',
      'onError',
    ];
    functionProps.forEach((prop) => {
      delete serializableConfig[prop];
    });

    return serializableConfig;
  }

  createIframe() {
    let container = document.createElement('div');
    let mountContainer = document.getElementById(this.config?.mountContainerId);
    let isNewContainer = false;

    // If a mountContainer is passed, use it
    if (!mountContainer) {
      // If no mount container is passed, create a new one
      mountContainer = document.createElement('div');
      mountContainer.classList.add('ramp-mount-container-overlay');
      isNewContainer = true;

      if (this.config.mountContainerId)
        mountContainer.id = this.config.mountContainerId;
    }

    // Modal overlay styles
    mountContainer.style.position = 'fixed';
    mountContainer.style.top = '0';
    mountContainer.style.bottom = '0';
    mountContainer.style.right = '0';
    mountContainer.style.height = '100%';
    mountContainer.style.width = '100%';
    mountContainer.style.background = 'rgba(0, 0, 0, 0.5)';
    mountContainer.style.display = 'flex';
    mountContainer.style.justifyContent = 'center';
    mountContainer.style.alignItems = 'center';
    mountContainer.style.zIndex = '9999';
    mountContainer.style.overflow = 'hidden';
    mountContainer.style.transition = 'opacity 0.3s ease-in-out';
    mountContainer.style.opacity = '1';
    mountContainer.style.pointerEvents = 'auto';
    mountContainer.style.backdropFilter = 'blur(5px)';
    mountContainer.style.webkitBackdropFilter = 'blur(5px)';

    // FIX 4: Use bound handler for overlay click and store reference
    mountContainer.addEventListener('click', this.boundHandleOverlayClick);

    // Set the container styles
    container.classList.add('ramp-container-style');

    container.style.maxWidth = '440px';
    container.style.width = '100%';
    container.style.overflow = 'hidden';
    container.style.padding = '15px';

    const iframe = document.createElement('iframe');
    iframe.src = environments.CHECKOUT_URL;
    // Set the iframe styles
    iframe.classList.add('ramp-iframe-style');
    iframe.style.height = '580px';

    iframe.style.width = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    iframe.id = 'RampIframe';
    iframe.allow = 'camera; microphone; clipboard-write; clipboard-read';

    container.appendChild(iframe);

    if (isNewContainer || mountContainer.childNodes.length === 0) {
      mountContainer.appendChild(container);
      document.body.appendChild(mountContainer);
    }
    this.modal = mountContainer;

    // Store the anonymous resize handler for cleanup
    this.iframeResizeHandler = (event) => {
      if (event.origin === environments.CHECKOUT_URL) {
        if (event.data.type === 'RESIZE_IFRAME') {
          container.style.height = `${event.data.height}px`;
        }
      }
    };

    iframe.onload = () => {
      iframe.contentWindow.postMessage(
        {
          ...this.getSerializableConfig(),
          type: 'INITIALIZE_QUIDAX_RAMP',
        },
        environments.CHECKOUT_URL, // FIX 5: Use specific origin
      );

      // Set up a listener for the iframe to send its height
      window.addEventListener('message', this.iframeResizeHandler);
    };
  }

  // FIX: New unified handlers for UI dismissal
  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.handleErrorAndCloseModal('Modal closed by pressing escape');
    }
  }

  handleOverlayClick(e) {
    if (e.target === this.modal) {
      this.handleErrorAndCloseModal('Modal closed by clicking overlay');
    }
  }

  // FIX: Replace setupCloseListener with a handler
  handleCloseMessage(event) {
    // FIX 2: Security check
    if (this.isValidMessage(event, 'closeQuidaxRampWidget')) {
      this.closeModal();
    }
  }

  // FIX: All setup methods are removed/replaced by setupListeners()

  closeModal() {
    if (this.modal) {
      document.body.removeChild(this.modal);
      this.modal = null;

      // Call destroy() to remove all listeners and prevent memory leaks
      this.destroyListeners();

      if (typeof this.config.onClose === 'function') {
        this.config.onClose(this.config.reference);
      }
    }
  }

  // 6. FIX: Add explicit listener cleanup method
  destroyListeners() {
    window.removeEventListener('message', this.boundHandleReadyMessage, false);
    window.removeEventListener('message', this.boundHandleSuccess);
    window.removeEventListener('message', this.boundHandleWalletDetails);
    window.removeEventListener('message', this.boundHandleCloseMessage);

    document.removeEventListener('keydown', this.boundHandleKeydown);

    // Remove click listener from the modal
    if (this.modal) {
      this.modal.removeEventListener('click', this.boundHandleOverlayClick);
    }

    // Remove the anonymous resize handler
    if (this.iframeResizeHandler) {
      window.removeEventListener('message', this.iframeResizeHandler);
      this.iframeResizeHandler = null;
    }
  }

  // 7. FIX: Public destroy method for consumers to explicitly clean up
  destroy() {
    this.closeModal(); // This will handle DOM removal and call destroyListeners()
  }

  handleReadyMessage(event) {
    // FIX 2: Security check
    if (this.isValidMessage(event, 'ready')) {
      const iframe = document.getElementById('RampIframe');
      iframe.contentWindow.postMessage(
        {
          ...this.getSerializableConfig(),
          type: 'INITIALIZE_QUIDAX_RAMP',
        },
        environments.CHECKOUT_URL, // FIX 5: Use specific origin
      );

      // FIX 8: Correctly remove the listener using the bound property
      window.removeEventListener(
        'message',
        this.boundHandleReadyMessage,
        false,
      );
    }
  }

  handleWalletDetails(event) {
    // FIX 2: Security check
    if (
      this.isValidMessage(event, 'walletDetails') &&
      typeof this.config.onReceiveWalletDetails === 'function'
    ) {
      this.config.onReceiveWalletDetails(event.data.payload);
    }
  }

  handleSuccess(event) {
    // FIX 2: Security check
    if (
      this.isValidMessage(event, 'quidaxRampTransactionSuccess') &&
      typeof this.config.onSuccess === 'function'
    ) {
      this.closeModal();
      this.config.onSuccess(event.data.payload);
    }
  }

  handleErrorAndCloseModal(error) {
    console.error('Ramp Error:', error);
    if (typeof this.config.onError === 'function') {
      this.config.onError(error);
    }
    // Always call closeModal, even if onError isn't defined
    this.closeModal();
  }

  // FIX: Removed the incorrect ResizeObserver/sendHeightToParent logic
  // as it belongs inside the iframe, not the host SDK.
}

window.ramp = new Ramp();
